import { axiosClientv1 } from "./apiClient";
import { ApiResponse } from "../Types";

export interface CreateOrderPayload {
  customer_id?: string | null;
  table_id?: string | null;
  selling_type?: string;
  subtotal: number;
  discount_amount?: number;
  other_charges?: number;
  tax_amount?: number;
  grand_total: number;
  paid_amount?: number;
  due_amount?: number;
  payment_method_id: string;
  payment_status_id: string;
  order_status_id: string;
  employee_id?: string | null;
  items: Array<{
    product_id: string;
    stock_id?: string | null;
    quantity: number;
    price: number;
    total_price?: number;
    discount_amount?: number;
    sold_price?: number;
  }>;
}
export interface UpdateOrderPayload extends CreateOrderPayload {
  id: string;
}

export const CreateOrder = async (
  payload: CreateOrderPayload,
): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.post(`/orders/create`, payload);
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message: error?.response?.data?.message || "Failed to create order.",
      error: {
        code: error?.response?.status || 400,
        type: "bad_request",
      },
    };
  }
};

export const UpdateOrder = async (
  payload: UpdateOrderPayload,
): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.post(`/orders/update`, payload);
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message: error?.response?.data?.message || "Failed to update order.",
      error: {
        code: error?.response?.status || 400,
        type: "bad_request",
      },
    };
  }
};

export const GetOrders = async (params: {
  page: number;
  limit: number;
  search?: string;
  customer_id?: string | null;
  payment_status_id?: string | null;
  order_status_id?: string | null;
  selling_type?: string | null;
  courier_status?: string | null;
}): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.get(`/orders`, { params });
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message: error?.response?.data?.message || "Failed to load orders.",
      data: { data: [], total: 0 },
      error: {
        code: error?.response?.status || 500,
        type: "server_error",
      },
    };
  }
};

export const GetOrderById = async (id: string): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.get(`/orders/view`, {
      params: { id },
    });
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message:
        error?.response?.data?.message || "Failed to load order details.",
      error: {
        code: error?.response?.status || 500,
        type: "server_error",
      },
    };
  }
};

export const GetPublicOrderById = async (
  tenantSlug: string,
  id: string,
): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.get(`/orders/public/view`, {
      params: { tenant_slug: tenantSlug, id },
    });
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message:
        error?.response?.data?.message ||
        "Failed to load public invoice details.",
      error: {
        code: error?.response?.status || 500,
        type: "server_error",
      },
    };
  }
};

export const GetOrderReturnById = async (
  id: string,
): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.get(`/orders/returns/view`, {
      params: { id },
    });
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message:
        error?.response?.data?.message ||
        "Failed to load order return details.",
      error: {
        code: error?.response?.status || 500,
        type: "server_error",
      },
    };
  }
};

export const GetPublicOrderReturnById = async (
  tenantSlug: string,
  id: string,
): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.get(`/orders/returns/public/view`, {
      params: { tenant_slug: tenantSlug, id },
    });
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message:
        error?.response?.data?.message ||
        "Failed to load public return invoice details.",
      error: {
        code: error?.response?.status || 500,
        type: "server_error",
      },
    };
  }
};

export const SendOrderInvoiceWhatsApp = async (payload: {
  order_id: string;
  phone: string;
  pdf_base64: string;
  filename?: string;
}): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.post(`/orders/send-invoice`, payload);
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message:
        error?.response?.data?.message ||
        "Failed to send invoice via WhatsApp.",
      error: {
        code: error?.response?.status || 502,
        type: "server_error",
      },
    };
  }
};

export const CreateOrderReturn = async (
  payload: any,
): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.post(`/orders/return`, payload);
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message:
        error?.response?.data?.message || "Failed to process order return.",
      error: {
        code: error?.response?.status || 400,
        type: "bad_request",
      },
    };
  }
};

export const DeleteOrder = async (id: string): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.post(`/orders/delete`, { id });
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message: error?.response?.data?.message || "Failed to delete order.",
      error: {
        code: error?.response?.status || 400,
        type: "bad_request",
      },
    };
  }
};

export const GetOrderReturns = async (params: {
  page: number;
  limit: number;
  search?: string;
  store_id?: string | null;
  customer_id?: string | null;
}): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.get(`/orders/returns`, { params });
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message:
        error?.response?.data?.message || "Failed to load order returns.",
      data: { data: [], total: 0 },
      error: {
        code: error?.response?.status || 500,
        type: "server_error",
      },
    };
  }
};
